import { UserProvider } from '@blocksuite/affine-shared/services';

import { EditorHostKey } from '../../../shared/editor-host-context.js';
import { parseCalendarAttendees } from '../../calendar-domain.js';
import type { CalendarSingleView } from '../../calendar-view-manager.js';
import { collectCalendarReconcileDocIds } from '../../core/calendar-reconcile-contract.js';
import type {
  CalendarDataSourceLike,
  CalendarEvent,
  CalendarReconcileColumns,
  CalendarReconcileContext,
  CalendarReconcileSnapshot,
} from '../calendar-view-types.js';
import {
  compactRecord,
  disposeCalendarSyncPriority,
  nullableText,
  stableSequence,
} from '../calendar-view-utils.js';

export async function flushCalendarReconcileDocs(
  context: CalendarReconcileContext,
  signal?: AbortSignal
): Promise<void> {
  const docEngine = context.docSyncEngine;
  if (!docEngine?.flushAndWaitForSynced) return;

  const disposers: unknown[] = [];
  for (const docId of context.syncDocIds) {
    docEngine.addDoc?.(docId);
    disposers.push(docEngine.addPriority?.(docId, 100));
  }

  try {
    for (const docId of context.syncDocIds) {
      signal?.throwIfAborted();
      await docEngine.flushAndWaitForSynced(docId, signal);
    }
  } finally {
    disposers.forEach(disposeCalendarSyncPriority);
  }
}

export function resolveCalendarReconcileContext(
  dataSource: unknown
): CalendarReconcileContext | null {
  const source = dataSource as CalendarDataSourceLike;
  const databaseId = source.databaseId ?? source.blockId;
  const docId = source.doc?.id;
  const workspaceId = source.doc?.workspace?.id;
  if (!databaseId || !docId || !workspaceId) return null;
  return {
    actorUserId: currentUserIdFromDataSource(source),
    databaseId,
    docId,
    docSyncEngine: source.doc?.workspace?.engine?.doc,
    syncDocIds: collectCalendarReconcileDocIds({
      databaseId,
      databaseRootDocId: source.storageModel?.store?.id,
      docId,
      storageModel: source.storageModel,
      workspaceId,
    }),
    workspaceId,
  };
}

function currentUserIdFromDataSource(
  source: CalendarDataSourceLike
): string | undefined {
  const host = source.serviceGet?.(EditorHostKey) as
    | { std?: { getOptional?: <T>(key: unknown) => T | undefined } }
    | undefined;
  const userService = host?.std?.getOptional?.<{
    currentUserInfo$: { value: { id?: string } | null };
  }>(UserProvider);
  const id = userService?.currentUserInfo$.value?.id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

export function calendarReconcileColumns(
  view: CalendarSingleView
): CalendarReconcileColumns | null {
  const startDateCol = view.startDateCol$.value;
  const endDateCol = view.endDateCol$.value;
  if (!startDateCol || !endDateCol) return null;
  return compactRecord({
    allDayCol: view.allDayCol$.value,
    attendeesCol: view.attendeesCol$.value,
    calendarIdCol: view.calendarIdCol$.value,
    descriptionCol: view.descriptionCol$.value,
    endDateCol,
    locationPhysicalCol: view.locationPhysicalCol$.value,
    locationVirtualCol: view.locationVirtualCol$.value,
    recurrenceCol: view.recurrenceCol$.value,
    recurrenceExdateCol: view.recurrenceExdateCol$.value,
    recurrenceIdCol: view.recurrenceIdCol$.value,
    reminderCol: view.reminderCol$.value,
    startDateCol,
    statusCol: view.statusCol$.value,
    timezoneCol: view.timezoneCol$.value,
    titleColumn: view.mainProperties$.value.titleColumn,
    visibilityCol: view.visibilityCol$.value,
  });
}

export function toCalendarReconcileSnapshot(
  event: CalendarEvent,
  context: CalendarReconcileContext,
  viewId: string
): CalendarReconcileSnapshot {
  return {
    allDay: event.allDay,
    attendees: parseCalendarAttendees(event.attendees).map(attendee => ({
      email: attendee.email,
      name: attendee.name,
      status: attendee.status,
      type: attendee.email ? 'external' : 'internal',
    })),
    calendarId: nullableText(event.calendarId),
    descriptionText: nullableText(event.description),
    endAt: event.endAt.toISOString(),
    physicalLocation: nullableText(event.locationPhysical),
    recurrenceExdate: nullableText(event.recurrenceExdate),
    recurrenceId: nullableText(event.recurrenceId),
    recurrenceRule: nullableText(event.recurrence),
    ref: {
      ...context,
      rowId: event.rowId,
      viewId,
    },
    reminder: nullableText(event.reminder),
    sequence: stableSequence([
      event.title,
      event.startAt.toISOString(),
      event.endAt.toISOString(),
      String(event.allDay),
      event.locationPhysical,
      event.locationVirtual,
      event.description,
      event.timezone,
      event.recurrence,
      event.recurrenceExdate,
      event.recurrenceId,
      event.calendarId,
      event.visibility,
      event.status,
    ]),
    startAt: event.startAt.toISOString(),
    status: nullableText(event.status),
    timezone: event.timezone || 'UTC',
    title: event.title,
    virtualLocation: nullableText(event.locationVirtual),
    visibility: nullableText(event.visibility),
  };
}
